<div class="dialog-header" mat-dialog-title>
    <div class="header-icon" aria-hidden="true">
        <mat-icon class="material-icons-outlined" aria-hidden="true">{{ isEdit ? 'edit_note' : 'post_add' }}</mat-icon>
    </div>
    <div class="header-copy">
        <h2>{{ isEdit ? ('EditElement' | translate: {Default: "Edit element"}) : ('AddElement' | translate: {Default: "Add element"}) }}</h2>
        <p>{{ 'FormElementDialogDescription' | translate: {Default: "Choose what the element is, what it's called and how it behaves."} }}</p>
    </div>
    <button type="button" mat-icon-button class="icon-button" appPreventDoubleClick (throttledClick)="close()" [attr.aria-label]="'Close' | translate: {Default: 'Close'}">
        <mat-icon class="material-icons-outlined" aria-hidden="true">close</mat-icon>
    </button>
</div>

<mat-dialog-content>
    <ng-container *ngIf="!form; else loadedFormElement">
        <ngx-skeleton-loader count="5" [theme]="{ 'height.px': 48, 'border-radius': '10px' }"></ngx-skeleton-loader>
    </ng-container>

    <ng-template #loadedFormElement>
        <form class="element-form" [formGroup]="form" (ngSubmit)="save()">

            <!-- Type and placement -->
            <section class="form-section">
                <div class="section-heading">
                    <span class="section-label">{{ 'TypeAndPlacement' | translate: {Default: "Type and placement"} }}</span>
                    <p>{{ 'TypeAndPlacementHint' | translate: {Default: "What kind of element this is and which section it sits in."} }}</p>
                </div>

                <div class="field-grid">
                    <mat-form-field appearance="fill">
                        <mat-label>{{ 'FormElementFormElementType' | translate: {Default: "Element type"} }}</mat-label>
                        <mat-select disableOptionCentering formControlName="formElementType" (selectionChange)="onFormElementTypeSelection($event)">
                            <app-select-search [search]="typeSearch"></app-select-search>
                            <mat-option *ngFor="let option of typeSearch.options" [value]="option.id">{{option.name}}</mat-option>
                        </mat-select>
                        <mat-error>{{ 'ChooseElementType' | translate: {Default: "Choose an element type"} }}</mat-error>
                    </mat-form-field>

                    <mat-form-field appearance="fill">
                        <mat-label>{{ 'Section' | translate: {Default: "Section"} }}</mat-label>
                        <mat-select disableOptionCentering formControlName="parentFormElement">
                            <mat-option value="">{{ 'NoSectionTopLevel' | translate: {Default: "None (top level)"} }}</mat-option>
                            <mat-option *ngFor="let item of parentElements" [value]="item.id">{{item.text}}</mat-option>
                        </mat-select>
                    </mat-form-field>
                </div>
            </section>

            <!-- Label -->
            <section class="form-section">
                <div class="section-heading">
                    <span class="section-label">{{ 'Label' | translate: {Default: "Label"} }}</span>
                    <p>{{ 'LabelHint' | translate: {Default: "The text users see. Add a translation for each language."} }}</p>
                </div>

                <app-localization-text-form-field
                    [formElement]="labelField"
                    [parentGroup]="form"
                    [cultures]="cultures"
                    [showTitle]="false"
                    (emitAddtoFormArray)="addLocalizationFromComponent($event)"
                    (emitDeleteFromFormArray)="deleteLocalizationFromComponent($event)"
                ></app-localization-text-form-field>
            </section>

            <!-- Linked table field -->
            <section class="form-section">
                <div class="section-heading">
                    <span class="section-label">{{ 'LinkedTableField' | translate: {Default: "Linked table field"} }}</span>
                    <p>{{ 'LinkedTableFieldHint' | translate: {Default: "Where the value is saved. Leave empty for elements that don't store data."} }}</p>
                </div>

                <mat-form-field appearance="fill">
                    <mat-label>{{ 'TableField' | translate: {Default: "Table field"} }}</mat-label>
                    <mat-select (selectionChange)="getTableFieldDetails()" disableOptionCentering formControlName="tableField">
                        <app-select-search [search]="tableFieldSearch"></app-select-search>
                        <mat-option [value]="null">{{ 'None' | translate: {Default: "None"} }}</mat-option>
                        <mat-option *ngFor="let item of tableFieldSearch.options" [value]="item.id">{{item.name}}</mat-option>
                    </mat-select>
                    <mat-icon class="material-icons-outlined" matPrefix aria-hidden="true">link</mat-icon>
                </mat-form-field>

                <ng-container *ngIf="formFieldId">
                    <ngx-skeleton-loader *ngIf="loadingTableField; else loadedTableField" count="3" [theme]="{ 'height.px': 44, 'border-radius': '10px' }"></ngx-skeleton-loader>

                    <ng-template #loadedTableField>
                        <div class="linked-field" *ngIf="selectedFieldVerbose">
                            <div class="callout">
                                <mat-icon class="material-icons-outlined" aria-hidden="true">info</mat-icon>
                                <p>{{ 'TableFieldPrecedence' | translate: {Default: "The table field's name, required and hidden settings override the ones set on this element."} }}</p>
                            </div>

                            <div class="linked-field-header">
                                <span class="section-label">{{ 'TableFieldSettings' | translate: {Default: "Table field settings"} }}</span>
                                <button type="button" mat-stroked-button class="rounded-button" setColor="primaryColour" color="primary" appPreventDoubleClick (throttledClick)="navigateToTableFieldEditor()">
                                    <mat-icon class="material-icons-outlined" aria-hidden="true">edit</mat-icon>
                                    {{ 'EditTableField' | translate: {Default: "Edit table field"} }}
                                </button>
                            </div>

                            <div class="linked-field-preview">
                                <app-form-generator
                                    [formId]="selectedFieldVerboseFormId"
                                    [formData]="selectedFieldVerboseFormData"
                                ></app-form-generator>
                            </div>
                        </div>
                    </ng-template>
                </ng-container>
            </section>

            <!-- Behaviour -->
            <section class="form-section">
                <div class="section-heading">
                    <span class="section-label">{{ 'Behaviour' | translate: {Default: "Behaviour"} }}</span>
                    <p>{{ 'BehaviourHint' | translate: {Default: "How the element acts for everyone who uses this form."} }}</p>
                </div>

                <div class="toggle-list">
                    <div class="toggle-row" *ngIf="propertyGroup('requiredField') as group" [formGroup]="group">
                        <div class="toggle-copy">
                            <span class="toggle-title">{{ 'Required' | translate: {Default: "Required"} }}</span>
                            <span class="toggle-hint">{{ 'RequiredHint' | translate: {Default: "Users must fill this in before they can save."} }}</span>
                        </div>
                        <mat-slide-toggle setColor="primaryColour" formControlName="value" [aria-label]="'Required' | translate: {Default: 'Required'}"></mat-slide-toggle>
                    </div>

                    <div class="toggle-row" *ngIf="propertyGroup('disabled') as group" [formGroup]="group">
                        <div class="toggle-copy">
                            <span class="toggle-title">{{ 'ReadOnly' | translate: {Default: "Read only"} }}</span>
                            <span class="toggle-hint">{{ 'ReadOnlyHint' | translate: {Default: "Users can see the value but not change it."} }}</span>
                        </div>
                        <mat-slide-toggle setColor="primaryColour" formControlName="value" [aria-label]="'ReadOnly' | translate: {Default: 'Read only'}"></mat-slide-toggle>
                    </div>

                    <div class="toggle-row" *ngIf="propertyGroup('hidden') as group" [formGroup]="group">
                        <div class="toggle-copy">
                            <span class="toggle-title">{{ 'Hidden' | translate: {Default: "Hidden"} }}</span>
                            <span class="toggle-hint">{{ 'HiddenHint' | translate: {Default: "The element isn't shown on the form."} }}</span>
                        </div>
                        <mat-slide-toggle setColor="primaryColour" formControlName="value" [aria-label]="'Hidden' | translate: {Default: 'Hidden'}"></mat-slide-toggle>
                    </div>
                </div>

                <div class="field-grid">
                    <mat-form-field appearance="fill" *ngIf="propertyGroup('minLength') as group" [formGroup]="group">
                        <mat-label>{{ 'MinimumValue' | translate: {Default: "Minimum value"} }}</mat-label>
                        <input matInput type="number" formControlName="value">
                    </mat-form-field>

                    <mat-form-field appearance="fill" *ngIf="propertyGroup('maxLength') as group" [formGroup]="group">
                        <mat-label>{{ 'MaximumValue' | translate: {Default: "Maximum value"} }}</mat-label>
                        <input matInput type="number" formControlName="value">
                    </mat-form-field>
                </div>

                <mat-form-field appearance="fill" *ngIf="propertyGroup('lookupGroup') as group" [formGroup]="group">
                    <mat-label>{{ 'DropdownList' | translate: {Default: "Dropdown list"} }}</mat-label>
                    <mat-select disableOptionCentering formControlName="value">
                        <ng-container *ngIf="tableLists | async as tableLists">
                            <mat-optgroup *ngFor="let tableList of tableLists" [label]="tableList.name">
                                <mat-option *ngFor="let item of tableList.fields" [value]="item.properties[1].value">
                                    {{item.name}}
                                </mat-option>
                            </mat-optgroup>
                        </ng-container>
                    </mat-select>
                    <mat-icon class="material-icons-outlined" matPrefix aria-hidden="true">list</mat-icon>
                    <mat-hint>{{ 'DropdownListHint' | translate: {Default: "The options users can choose from."} }}</mat-hint>
                </mat-form-field>
            </section>

            <!-- Help text -->
            <section class="form-section">
                <div class="section-heading with-action">
                    <div>
                        <span class="section-label">{{ 'HelpText' | translate: {Default: "Help text"} }}</span>
                        <p>{{ 'HelpTextHint' | translate: {Default: "Shown in a tooltip next to the element."} }}</p>
                    </div>
                    <button
                        *ngIf="tooltipLocalizations.length > 0"
                        type="button"
                        class="add-link danger"
                        appPreventDoubleClick (throttledClick)="deleteAllTooltipLocalizations()">
                        {{ 'RemoveHelpText' | translate: {Default: "Remove help text"} }}
                    </button>
                </div>

                <app-localization-text-form-field
                    *ngIf="tooltipLocalizations.length > 0; else noHelpText"
                    [formElement]="helpTextField"
                    [parentGroup]="form"
                    [cultures]="cultures"
                    [showTitle]="false"
                    (emitAddtoFormArray)="addLocalizationFromComponent($event)"
                    (emitDeleteFromFormArray)="deleteLocalizationFromComponent($event)"
                ></app-localization-text-form-field>

                <ng-template #noHelpText>
                    <button type="button" class="add-link" appPreventDoubleClick (throttledClick)="addNewTooltipLocalization()">
                        <mat-icon class="material-icons-outlined" aria-hidden="true">add</mat-icon>
                        {{ 'AddHelpText' | translate: {Default: "Add help text"} }}
                    </button>
                </ng-template>
            </section>

            <!-- Advanced -->
            <mat-expansion-panel class="advanced-panel" [(expanded)]="advancedOpen">
                <mat-expansion-panel-header>
                    <mat-panel-title>
                        <mat-icon class="material-icons-outlined" aria-hidden="true">tune</mat-icon>
                        {{ 'Advanced' | translate: {Default: "Advanced"} }}
                    </mat-panel-title>
                    <mat-panel-description>
                        <span *ngIf="customPropertiesInvalid && showErrors; else advancedHint" class="error-text">
                            {{ 'AdvancedHasErrors' | translate: {Default: "Some properties need attention"} }}
                        </span>
                        <ng-template #advancedHint>{{ 'AdvancedHint' | translate: {Default: "Control name and custom properties"} }}</ng-template>
                    </mat-panel-description>
                </mat-expansion-panel-header>

                <div class="advanced-content">
                    <div class="field-grid">
                        <mat-form-field appearance="fill" *ngIf="propertyGroup('formControl') as group" [formGroup]="group">
                            <mat-label>{{ 'FormControlName' | translate: {Default: "Form control name"} }}</mat-label>
                            <input matInput formControlName="value">
                        </mat-form-field>

                        <mat-form-field appearance="fill" *ngIf="isEdit">
                            <mat-label>{{ 'ElementId' | translate: {Default: "Element ID"} }}</mat-label>
                            <input matInput [value]="formElementId" readonly>
                        </mat-form-field>
                    </div>

                    <span class="section-label">{{ 'CustomProperties' | translate: {Default: "Custom properties"} }}</span>

                    <p class="empty-hint" *ngIf="customPropertyIndexes.length === 0">
                        {{ 'NoCustomProperties' | translate: {Default: "No custom properties."} }}
                    </p>

                    <ng-container formArrayName="properties">
                        <div class="property-row" *ngFor="let i of customPropertyIndexes" [formGroupName]="i">
                            <mat-form-field appearance="fill">
                                <mat-label>{{ 'Property' | translate: {Default: "Property"} }}</mat-label>
                                <input matInput formControlName="property">
                                <mat-error>{{ 'Required' | translate: {Default: "Required"} }}</mat-error>
                            </mat-form-field>

                            <mat-form-field appearance="fill">
                                <mat-label>{{ 'Value' | translate: {Default: "Value"} }}</mat-label>
                                <input matInput formControlName="value">
                            </mat-form-field>

                            <button
                                type="button"
                                mat-icon-button
                                class="icon-button danger row-delete"
                                appPreventDoubleClick (throttledClick)="deleteProperty(i)"
                                [matTooltip]="'RemoveProperty' | translate: {Default: 'Remove property'}" [attr.aria-label]="'RemoveProperty' | translate: {Default: 'Remove property'}">
                                <mat-icon class="material-icons-outlined" aria-hidden="true">delete</mat-icon>
                            </button>
                        </div>
                    </ng-container>

                    <button type="button" class="add-link" appPreventDoubleClick (throttledClick)="addCustomProperty()">
                        <mat-icon class="material-icons-outlined" aria-hidden="true">add</mat-icon>
                        {{ 'AddProperty' | translate: {Default: "Add property"} }}
                    </button>
                </div>
            </mat-expansion-panel>
        </form>
    </ng-template>
</mat-dialog-content>

<mat-dialog-actions>
    <span class="footer-error" *ngIf="showErrors && form?.invalid">
        <mat-icon class="material-icons-outlined" aria-hidden="true">error_outline</mat-icon>
        {{ 'FixHighlightedFields' | translate: {Default: "Fix the highlighted fields to save"} }}
    </span>
    <button mat-stroked-button class="rounded-button" appPreventDoubleClick (throttledClick)="close()">
        {{ 'Cancel' | translate: {Default: "Cancel"} }}
    </button>
    <button
        *ngIf="form"
        mat-flat-button
        class="rounded-button"
        setColor="primaryColour"
        color="primary"
        [disabled]="saving"
        appPreventDoubleClick (throttledClick)="save()">
        <mat-spinner *ngIf="saving" diameter="16" strokeWidth="2"></mat-spinner>
        {{ isEdit ? ('SaveChanges' | translate: {Default: "Save changes"}) : ('AddElement' | translate: {Default: "Add element"}) }}
    </button>
</mat-dialog-actions>
